import type { ReactNode } from 'react';
import { podeFazer, primeiroNome } from '../acesso';
import type { Eu } from '../acesso';
import { Comemoracoes } from '../design/Comemoracoes';
import { FotoOuIniciais } from '../design/FotoDePerfil';
import { FaixaDeConexao } from '../design/Estados';
import { AvisoCritico } from '../telas/balconista/AvisoCritico';
import { Icone } from '../design/Icone';
import { Sino } from './Sino';
import type { NomeDoIcone } from '../design/Icone';
import { UNIDADE } from '../telas/equipe/unidade';
import { TourDeBoasVindas } from '../telas/balconista/TourDeBoasVindas';
import './balconista.css';

/**
 * TELA 06 (parte 02) — O SHELL DO BALCONISTA. "O balconista não herda a sidebar do admin."
 *
 *   < 1024   bottom nav (Início · Desempenho · Encomendas · VENDER · Giro · Conta): o Vender
 *            maior e vermelho; rótulo sempre visível. Encomendas só para quem consulta
 *            (dono, 02/10: sai do Início e vira item do menu, ao lado do Desempenho);
 *            alvos de 62px na metade de baixo da tela. Cada página desenha o próprio topo.
 *   ≥ 1024   rail curto de 5 itens (72px; 88px a partir de 1440) + topbar com busca.
 *
 * 🚨 Nada inventado: sem ponto vermelho no Giro nem número no sino enquanto não houver missão ou
 * notificação de verdade.
 */

export type RotaDoBalconista =
  'inicio' | 'vendas' | 'giro' | 'conta' | 'desempenho' | 'minhas' | 'pagando' | 'encomendas';

const ITENS: readonly {
  rota: RotaDoBalconista;
  rotulo: string;
  icone: NomeDoIcone;
  /** O botão do meio, maior e vermelho (dono, 25/09): vender é a ação principal. */
  destaque?: boolean;
}[] = [
  { rota: 'inicio', rotulo: 'Início', icone: 'grid' },
  { rota: 'desempenho', rotulo: 'Desempenho', icone: 'gauge' },
  { rota: 'encomendas', rotulo: 'Encomendas', icone: 'box' },
  { rota: 'vendas', rotulo: 'Vender', icone: 'receipt', destaque: true },
  { rota: 'giro', rotulo: 'Giro', icone: 'cycle' },
  { rota: 'conta', rotulo: 'Conta', icone: 'user' },
];

/** Qual aba acende: "o que está pagando" mora em Vender; minhas vendas, no Desempenho. */
function abaDe(rota: RotaDoBalconista): RotaDoBalconista {
  if (rota === 'pagando') return 'vendas';
  if (rota === 'minhas') return 'desempenho';
  return rota;
}

export function saudacao(agora = new Date()): string {
  const h = Number(
    agora.toLocaleString('pt-BR', {
      hour: '2-digit',
      hour12: false,
      timeZone: 'America/Sao_Paulo',
    }),
  );
  return h < 12 ? 'Bom dia' : h < 18 ? 'Boa tarde' : 'Boa noite';
}

export function papelDoBalconista(eu: Eu | null): string {
  if (!eu) return 'Carregando…';
  const papel = eu.perfis.map((p) => p.nome).join(' · ') || 'Balconista';
  return `${papel} · cód ${eu.login} · ${UNIDADE.replace('Rede Inova · ', 'Inova ')}`;
}

export function ShellBalconista({
  rota,
  eu,
  titulo,
  children,
}: {
  rota: RotaDoBalconista;
  eu: Eu | null;
  /** Título da topbar no PC. Sem título: a saudação (tela Início). */
  titulo: string | null;
  children: ReactNode;
}) {
  const nome = eu ? eu.nomeExibicao || eu.nome : null;
  const aba = abaDe(rota);
  const itens = ITENS.filter(
    (i) => i.rota !== 'encomendas' || (eu !== null && podeFazer(eu, 'encomendas.consultar')),
  );

  return (
    <div className="bs">
      <aside className="bs-rail" aria-label="Menu">
        <a className="bs-rail-marca" href="#/inicio" aria-label="InovaPay — Início">
          <img className="bs-rail-logo" src="/img/icon-192.png" alt="" />
        </a>
        <nav className="bs-rail-nav">
          {itens.map((i) => (
            <a
              key={i.rota}
              href={`#/${i.rota}`}
              className={`bs-rail-item${aba === i.rota ? ' bs-ativo' : ''}`}
              data-tour={`nav-${i.rota}`}
              aria-current={aba === i.rota ? 'page' : undefined}
            >
              <Icone nome={i.icone} tamanho={20} traco={1.5} />
              <span>{i.rotulo}</span>
            </a>
          ))}
        </nav>
        <a className="bs-rail-avatar" href="#/conta" aria-label="Minha conta">
          {nome ? (
            <FotoOuIniciais foto={eu?.foto} nome={nome} />
          ) : (
            <Icone nome="user" tamanho={15} />
          )}
        </a>
      </aside>

      <div className="bs-principal">
        <header className="bs-topo">
          {titulo === null ? (
            <div className="bs-topo-ola">
              <div className="bs-topo-nome">
                {nome ? `${saudacao()}, ${primeiroNome(nome)}` : saudacao()}
              </div>
              <div className="bs-topo-papel">{papelDoBalconista(eu)}</div>
            </div>
          ) : (
            <div className="bs-topo-titulo">{titulo}</div>
          )}
          <div style={{ flex: 1 }} />
          <a className="bs-topo-busca" href="#/vendas?buscar=1">
            <Icone nome="search" tamanho={15} traco={1.6} />
            <span>Buscar produto ou EAN…</span>
          </a>
          {titulo === null ? (
            <Sino eu={eu} classe="bs-sino" painelClasse="sino-caixa" />
          ) : (
            <a className="bs-topo-conta" href="#/conta">
              <span className="bs-avatar-mini">
                {nome ? <FotoOuIniciais foto={eu?.foto} nome={nome} /> : ''}
              </span>
              <span>{nome ? primeiroNome(nome) : 'Sua conta'}</span>
            </a>
          )}
        </header>
        <main className="bs-conteudo" id="conteudo" tabIndex={-1}>
          <FaixaDeConexao />
          {children}
        </main>
      </div>

      <Comemoracoes />
      <AvisoCritico login={eu?.login ?? null} />
      <TourDeBoasVindas eu={eu} />

      <nav className="bs-baixo" aria-label="Menu">
        <div
          className={`bs-baixo-itens${itens.length > 5 ? ' bs-baixo-seis' : ''}`}
          style={{ gridTemplateColumns: `repeat(${String(itens.length)}, 1fr)` }}
        >
          {itens.map((i) => (
            <a
              key={i.rota}
              href={`#/${i.rota}`}
              className={`bs-baixo-item${aba === i.rota ? ' bs-ativo' : ''}${i.destaque ? ' bs-vender' : ''}`}
              data-tour={`nav-${i.rota}`}
              aria-current={aba === i.rota ? 'page' : undefined}
            >
              {i.destaque ? (
                <span className="bs-vender-bola">
                  <Icone nome={i.icone} tamanho={24} traco={1.7} />
                </span>
              ) : (
                <Icone nome={i.icone} tamanho={20} traco={1.5} />
              )}
              <span>{i.rotulo}</span>
            </a>
          ))}
        </div>
      </nav>
    </div>
  );
}

/** O topo das telas no celular/tablet (no PC quem mostra é a topbar do shell). */
export function TopoMovel({
  eu,
  voltar,
  titulo,
  direita,
}: {
  eu: Eu | null;
  /** Com `voltar`: seta + título (tela de tarefa). Sem: saudação + sino (Início). */
  voltar?: string;
  titulo?: string;
  direita?: ReactNode;
}) {
  const nome = eu ? eu.nomeExibicao || eu.nome : null;
  if (voltar) {
    return (
      <div className="bs-movel-topo bs-movel-tarefa">
        <a className="bs-voltar" href={voltar} aria-label="Voltar">
          <Icone nome="arrow-l" tamanho={20} traco={1.6} />
        </a>
        <div className="bs-movel-titulo">{titulo}</div>
        {direita}
      </div>
    );
  }
  return (
    <div className="bs-movel-topo bs-movel-marca">
      <div className="bs-movel-linha">
        <img className="bs-movel-logo" src="/img/inovapay-logo.png" alt="InovaPay" />
        <div style={{ flex: 1 }} />
        {direita}
        <Sino eu={eu} classe="bs-movel-sino" painelClasse="sino-caixa" />
        <a className="bs-movel-avatar" href="#/conta" aria-label="Minha conta">
          {nome ? (
            <FotoOuIniciais foto={eu?.foto} nome={nome} />
          ) : (
            <Icone nome="user" tamanho={15} />
          )}
        </a>
      </div>
      <div className="bs-movel-ola">
        <div className="bs-movel-nome">
          {nome ? `${saudacao()}, ${primeiroNome(nome)}` : saudacao()}
        </div>
        <div className="bs-movel-papel">{papelDoBalconista(eu)}</div>
      </div>
    </div>
  );
}
